import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Loader2, Percent, Trash2, Plus } from "lucide-react";

const PLANS = [
  { key: "lite_annual", label: "Lite (Standard) — Annual" },
  { key: "pro_annual", label: "Pro — Annual" },
  { key: "lite_monthly", label: "Lite (Standard) — Monthly" },
  { key: "pro_monthly", label: "Pro — Monthly" },
];
const PLAN_LABEL = Object.fromEntries(PLANS.map((p) => [p.key, p.label]));

const EMPTY = {
  name: "", discount_type: "percent", value: 25, duration: "once", duration_in_months: 3,
  scope: "auto", plan_lookup_keys: ["lite_annual", "pro_annual"], target_emails: "",
  starts_at: "", ends_at: "",
};

export default function AdminPromotions() {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [rowBusy, setRowBusy] = useState("");

  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/promotions"); setRows(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to load promotions."); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const togglePlan = (k) => setForm((f) => ({
    ...f, plan_lookup_keys: f.plan_lookup_keys.includes(k) ? f.plan_lookup_keys.filter((x) => x !== k) : [...f.plan_lookup_keys, k],
  }));

  const create = async () => {
    if (!form.name.trim()) return toast.error("Give the promotion a name.");
    if (!form.value || Number(form.value) <= 0) return toast.error("Enter a discount value greater than 0.");
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(), discount_type: form.discount_type, value: Number(form.value),
        duration: form.duration, duration_in_months: form.duration === "repeating" ? Number(form.duration_in_months) : null,
        scope: form.scope, plan_lookup_keys: form.plan_lookup_keys,
        target_emails: form.scope === "targeted" ? form.target_emails.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean) : [],
        starts_at: form.starts_at ? new Date(form.starts_at).toISOString() : null,
        ends_at: form.ends_at ? new Date(form.ends_at).toISOString() : null,
      };
      await api.post("/admin/promotions", payload);
      toast.success("Promotion created and live.");
      setForm(EMPTY);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to create promotion."); }
    finally { setBusy(false); }
  };

  const patch = async (id, body, label) => {
    setRowBusy(id);
    try { await api.put(`/admin/promotions/${id}`, body); toast.success(label || "Updated."); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to update."); }
    finally { setRowBusy(""); }
  };

  const remove = async (id) => {
    if (!window.confirm("Stop and remove this promotion? New checkouts won't get it. Discounts already applied to existing subscribers continue per Stripe.")) return;
    setRowBusy(id);
    try { await api.delete(`/admin/promotions/${id}`); toast.success("Promotion stopped."); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to remove."); }
    finally { setRowBusy(""); }
  };

  const discountText = (r) => r.discount_type === "percent" ? `${r.value}% off` : `$${r.value} off`;
  const durationText = (r) => r.duration === "once" ? "first payment" : r.duration === "forever" ? "forever" : `${r.duration_in_months} months`;

  return (
    <div className="max-w-4xl space-y-6" data-testid="admin-promotions">
      <div className="flex items-center gap-2">
        <Percent className="h-5 w-5 text-[#1a3060]" />
        <h1 className="font-heading text-2xl font-bold">Promotions</h1>
      </div>
      <p className="text-sm text-muted-foreground -mt-3">Create Stripe-backed discounts. Auto campaigns apply to selected plans at checkout for everyone; targeted promos apply only to the users you list. Adjust or stop any promo anytime.</p>

      {/* Create form */}
      <Card className="border-zinc-200"><CardContent className="p-5 space-y-4">
        <p className="font-semibold flex items-center gap-2"><Plus className="h-4 w-4" /> New promotion</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-xs font-semibold uppercase tracking-wider">Name</Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Annual launch 25% off" data-testid="promo-name" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">Discount type</Label>
            <select className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm" value={form.discount_type} onChange={(e) => set("discount_type", e.target.value)} data-testid="promo-type">
              <option value="percent">Percentage off</option>
              <option value="amount">Fixed amount off (USD)</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">{form.discount_type === "percent" ? "Percent (%)" : "Amount (USD)"}</Label>
            <Input type="number" min="1" value={form.value} onChange={(e) => set("value", e.target.value)} data-testid="promo-value" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">Lasts</Label>
            <select className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm" value={form.duration} onChange={(e) => set("duration", e.target.value)} data-testid="promo-duration">
              <option value="once">First payment only</option>
              <option value="repeating">A set number of months</option>
              <option value="forever">Forever (every renewal)</option>
            </select>
          </div>
          {form.duration === "repeating" && (
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider">Months</Label>
              <Input type="number" min="1" value={form.duration_in_months} onChange={(e) => set("duration_in_months", e.target.value)} data-testid="promo-months" />
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">Who gets it</Label>
            <select className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm" value={form.scope} onChange={(e) => set("scope", e.target.value)} data-testid="promo-scope">
              <option value="auto">Everyone (auto-applied at checkout)</option>
              <option value="targeted">Specific users only</option>
            </select>
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-xs font-semibold uppercase tracking-wider">Applies to plans</Label>
          <div className="flex flex-wrap gap-2">
            {PLANS.map((p) => (
              <button type="button" key={p.key} onClick={() => togglePlan(p.key)} data-testid={`promo-plan-${p.key}`}
                className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${form.plan_lookup_keys.includes(p.key) ? "bg-[#1a3060] text-white border-[#1a3060]" : "bg-white text-muted-foreground border-zinc-200"}`}>
                {p.label}
              </button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">Leave all unselected to apply to every plan.</p>
        </div>

        {form.scope === "targeted" && (
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">Target user emails</Label>
            <textarea rows={2} className="w-full px-3 py-2 rounded-md border border-input bg-background text-sm" placeholder="One email per line (or comma-separated)" value={form.target_emails} onChange={(e) => set("target_emails", e.target.value)} data-testid="promo-emails" />
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">Campaign starts (optional)</Label>
            <Input type="date" value={form.starts_at} onChange={(e) => set("starts_at", e.target.value)} data-testid="promo-starts" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">Campaign ends (optional)</Label>
            <Input type="date" value={form.ends_at} onChange={(e) => set("ends_at", e.target.value)} data-testid="promo-ends" />
          </div>
        </div>

        <Button onClick={create} disabled={busy} className="bg-[#1a3060] hover:bg-[#12244a]" data-testid="promo-create">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create promotion"}
        </Button>
      </CardContent></Card>

      {/* List */}
      <div className="space-y-3">
        <p className="font-semibold">Active & past promotions</p>
        {!rows ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No promotions yet.</p>
        ) : rows.map((r) => (
          <Card key={r.id} className="border-zinc-200" data-testid={`promo-row-${r.id}`}><CardContent className="p-4 flex flex-wrap items-center gap-4 justify-between">
            <div className="min-w-0">
              <p className="font-semibold flex items-center gap-2">
                {r.name}
                <span className={`text-[11px] px-2 py-0.5 rounded-full ${r.active ? "bg-green-100 text-green-800" : "bg-zinc-100 text-zinc-500"}`}>{r.active ? "Active" : "Stopped"}</span>
              </p>
              <p className="text-sm text-muted-foreground">
                {discountText(r)} · {durationText(r)} · {r.scope === "auto" ? "everyone" : `${(r.target_emails || []).length} users`}
                {" · "}{(r.plan_lookup_keys && r.plan_lookup_keys.length) ? r.plan_lookup_keys.map((k) => PLAN_LABEL[k] || k).join(", ") : "all plans"}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Redeemed {r.times_redeemed ?? "—"} times
                {r.ends_at ? ` · ends ${new Date(r.ends_at).toLocaleDateString()}` : ""}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {r.discount_type === "percent" && r.active && (
                <div className="flex items-center gap-1.5">
                  <Input type="number" min="1" defaultValue={r.value} className="w-20 h-9"
                    onKeyDown={(e) => { if (e.key === "Enter") patch(r.id, { value: Number(e.currentTarget.value) }, "Discount updated."); }}
                    onBlur={(e) => { if (Number(e.target.value) !== r.value) patch(r.id, { value: Number(e.target.value) }, "Discount updated."); }}
                    data-testid={`promo-adjust-${r.id}`} />
                  <span className="text-sm text-muted-foreground">%</span>
                </div>
              )}
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-muted-foreground">{r.active ? "On" : "Off"}</span>
                <Switch checked={!!r.active} disabled={rowBusy === r.id} onCheckedChange={(v) => patch(r.id, { active: v }, v ? "Promotion resumed." : "Promotion paused.")} data-testid={`promo-active-${r.id}`} />
              </div>
              <Button variant="ghost" size="icon" disabled={rowBusy === r.id} onClick={() => remove(r.id)} data-testid={`promo-delete-${r.id}`} className="text-destructive hover:text-destructive">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </CardContent></Card>
        ))}
      </div>
    </div>
  );
}
